import { Box, Group, UnstyledButton } from "@mantine/core"
import type { MouseEvent, ReactNode } from "react"

import { CivalaMark } from "../CivalaMark"
import { CivalaWordmark } from "../CivalaWordmark"

// Canonical Civala marketing chrome — sticky 56px navy-on-white header.
//
// Router-agnostic: nav items are passed in by the consumer. Inside a
// TanStack Router context (e.g., apps/invest), wrap with an adapter that
// reads useRouterState + useNavigate and translates to nav items with
// onClick handlers. Outside a router (e.g., apps/landing), pass plain
// hrefs and omit onClick.

export type TopNavItem = {
  /** Unique id; used as React key. */
  id: string
  /** Visible label rendered in caps in the header. */
  label: string
  /** Anchor href — used for native navigation if onClick is not provided. */
  href: string
  /** Whether this item represents the current page (renders primary text color). */
  active?: boolean
  /** Optional click handler — when provided, default anchor nav is prevented. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

export type TopNavProps = {
  navItems: ReadonlyArray<TopNavItem>
  /** Brand wordmark href. Defaults to "/". */
  brandHref?: string
  /** Optional click handler for the brand wordmark. */
  onBrandClick?: (event: MouseEvent<HTMLAnchorElement>) => void
  /** Optional slot rendered at the right of the header (e.g., invest's audience SegmentedControl). */
  audienceControl?: ReactNode
}

export function TopNav({ navItems, brandHref = "/", onBrandClick, audienceControl }: TopNavProps) {
  return (
    <Box
      component="header"
      h={56}
      bg="var(--pitch-surface)"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 40,
        borderBottom: "1px solid var(--pitch-border)",
      }}
    >
      <Group h="100%" px="lg" justify="space-between" maw={1440} mx="auto" wrap="nowrap">
        <UnstyledButton
          component="a"
          href={brandHref}
          onClick={(e) => {
            if (onBrandClick) {
              e.preventDefault()
              onBrandClick(e)
            }
          }}
        >
          <Group gap={8} align="center" wrap="nowrap">
            <CivalaMark size={18} />
            <CivalaWordmark height={18} variant="ink" />
          </Group>
        </UnstyledButton>

        <Group gap={28} visibleFrom="md">
          {navItems.map((n) => (
            <UnstyledButton
              key={n.id}
              component="a"
              href={n.href}
              onClick={(e) => {
                if (n.onClick) {
                  e.preventDefault()
                  n.onClick(e)
                }
              }}
              fz={11}
              lts="0.08em"
              tt="uppercase"
              fw={500}
              c={n.active ? "var(--pitch-text-primary)" : "var(--pitch-text-secondary)"}
            >
              {n.label}
            </UnstyledButton>
          ))}
        </Group>

        {audienceControl}
      </Group>
    </Box>
  )
}
